import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Spinner, Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CandidateService from './CandidateService';
import AddCandidatesModal from './AddCandidatesModal';


const CandidatePipeline = ({ roleIdentifier }) => {
  const [draggableData, setDraggableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        const data = await CandidateService.getCandidatesByStage(roleIdentifier);
        setDraggableData(data);
      } catch (error) {
        console.error('Error fetching candidates:', error);
      } finally {
        setLoading(false);
      }
    };

    if (roleIdentifier) {
      fetchCandidates();
    }
  }, [roleIdentifier]);

  const getColumn = id => {
    return draggableData.find(item => item.id === id);
  };

  const reorder = (array, fromIndex, toIndex) => {
    const newArr = [...array];
    const chosenItem = newArr.splice(fromIndex, 1)[0];
    newArr.splice(toIndex, 0, chosenItem);
    return newArr;
  };

  const move = (source, destination) => {
    const sourceItemsClone = [...getColumn(source.droppableId).contents];
    const destItemsClone = [...getColumn(destination.droppableId).contents];

    const [removedItem] = sourceItemsClone.splice(source.index, 1);
    destItemsClone.splice(destination.index, 0, removedItem);

    return {
      updatedDestItems: destItemsClone,
      updatedSourceItems: sourceItemsClone
    };
  };

  const onDragEnd = result => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      // Reordering within the same column
      const items = getColumn(source.droppableId).contents;
      const reorderedItems = reorder(items, source.index, destination.index);
      const updatedData = draggableData.map(item =>
        item.id === source.droppableId
          ? { ...item, contents: reorderedItems }
          : item
      );
      setDraggableData(updatedData);
    } else {
      // Moving between columns
      const sourceColumn = getColumn(source.droppableId);
      const destColumn = getColumn(destination.droppableId);
      const movedItems = move(source, destination);
      const updatedData = draggableData.map(item => {
        if (item.id === sourceColumn.id) {
          return { ...item, contents: movedItems.updatedSourceItems };
        } else if (item.id === destColumn.id) {
          return { ...item, contents: movedItems.updatedDestItems };
        }
        return item;
      });

      setDraggableData(updatedData);

      // Extract candidate ID from the draggable item
      const candidateId = result.draggableId.replace('drag-', '');
      
      // Update backend (optional - can be called asynchronously)
      CandidateService.updateCandidateStage(roleIdentifier, candidateId, destination.droppableId)
        .then(response => {
          console.log('Candidate stage updated:', response);
        })
        .catch(error => {
          console.error('Error updating candidate stage:', error);
        });
    }
  };

  const handleCandidatesAdded = (newCandidates) => {
    // Add new candidates to the first stage (Submitting Application)
    setDraggableData(prevData => {
      const updatedData = [...prevData];
      if (updatedData.length > 0 && updatedData[0].id === 'submitting-application') {
        updatedData[0] = {
          ...updatedData[0],
          contents: [...updatedData[0].contents, ...newCandidates]
        };
      }
      return updatedData;
    });
  };

  const handleRemoveCandidate = (candidateId, roleIdentifier, stageId) => {
    // Remove candidate from the specified stage
    setDraggableData(prevData => {
      return prevData.map(stage => {
        if (stage.id === stageId) {
          return {
            ...stage,
            contents: stage.contents.filter(candidate => candidate.id !== candidateId)
          };
        }
        return stage;
      });
    });
    // Call backend service to remove candidate
    CandidateService.removeCandidate(roleIdentifier, candidateId)
      .then(response => {
        console.log('Candidate removed:', response);
      })
      .catch(error => {
        console.error('Error removing candidate:', error);
      });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading candidates...</span>
        </Spinner>
      </div>
    );
  }

  if (draggableData.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-muted">No candidates found for this position.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3 mt-2">
      <Card.Header>
        <h5 className="mb-0">Candidate Pipeline</h5>
        <p className="text-muted mb-0 fs--1">
          Drag and drop candidates between stages to manage the recruitment process
        </p>
      </Card.Header>
      <Card.Body>
        <DragDropContext onDragEnd={onDragEnd}>
          <Row>
            {draggableData.map((stage, stageIndex) => (
              <Col lg={3} md={6} key={stage.id} className="mb-3">
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase fw-semi-bold fs--1 mb-0">
                      {stage.title}
                    </h6>
                    <small className="text-muted">
                      {stage.contents.length} candidate{stage.contents.length !== 1 ? 's' : ''}
                    </small>
                  </div>
                  {stage.id === 'submitting-application' && (
                    <Button
                      size="sm"
                      variant="falcon-primary"
                      onClick={() => setShowAddModal(true)}
                      title="Add candidates"
                    >
                      <FontAwesomeIcon icon="plus" />
                    </Button>
                  )}
                </div>
                <Droppable droppableId={stage.id} type="CANDIDATE">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`kanban-items-container border rounded-2 py-3 px-2 ${
                        snapshot.isDraggingOver
                          ? 'bg-light'
                          : 'bg-white dark__bg-1000'
                      }`}
                      style={{
                        minHeight: '200px',
                        maxHeight: '600px',
                        overflowY: 'auto'
                      }}
                    >
                      {stage.contents.map((candidate, index) => (
                        <Draggable
                          key={candidate.id}
                          draggableId={`drag-${candidate.id}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <Card
                                className={`mb-2 kanban-item shadow-sm ${
                                  snapshot.isDragging
                                    ? 'shadow-lg'
                                    : 'dark__bg-1100'
                                }`}
                                style={{
                                  cursor: 'grab',
                                  opacity: snapshot.isDragging ? 0.8 : 1
                                }}
                              >
                                <Card.Body className="p-2 d-flex justify-content-between align-items-center">
                                  <p className="fs--1 fw-medium font-sans-serif mb-0">
                                    {candidate.text}
                                  </p>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0 ms-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Remove ${candidate.text} from this stage?`)) {
                                        handleRemoveCandidate(candidate.id, roleIdentifier, stage.id);
                                      }
                                    }}
                                    title="Remove candidate"
                                    style={{ minWidth: 'auto' }}
                                  >
                                    <FontAwesomeIcon icon="times" />
                                  </Button>
                                </Card.Body>
                              </Card>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {stage.contents.length === 0 && (
                        <div className="text-center text-muted py-3">
                          <small>No candidates in this stage</small>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Col>
            ))}
          </Row>
        </DragDropContext>
      </Card.Body>
      <AddCandidatesModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        roleIdentifier={roleIdentifier}
        onCandidatesAdded={handleCandidatesAdded}
      />
    </Card>
  );
};

export default CandidatePipeline;
